import { trackApiCall } from './monitoring';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './apiConfig';
import { getCsrfToken, needsCsrfToken } from './csrfToken';

// Enhanced fetch with automatic monitoring
export const monitoredFetch = async (url, options = {}) => {
  const startTime = Date.now();
  const method = options.method || 'GET';
  
  try {
    const response = await fetch(url, options);
    const duration = Date.now() - startTime;
    
    // Track successful API call
    trackApiCall(url, method, duration, response.status);
    
    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    
    // Track failed API call
    trackApiCall(url, method, duration, 0, error);
    
    throw error;
  }
};

// API client with monitoring
export class ApiClient {
  constructor(baseURL = getApiBaseUrl()) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
    const isFormData = options.body instanceof FormData;
    
    // Attach token if available
    let token = null;
    try {
      // Try to get token from localStorage
      token = localStorage.getItem('access_token');
    } catch (error) {
      console.error('Error retrieving token from localStorage:', error);
    }
    
    // Get CSRF token for state-changing operations using centralized utility
    let csrfToken = null;
    const method = (options.method || 'GET').toUpperCase();
    if (needsCsrfToken(method)) {
      try {
        csrfToken = await getCsrfToken();
      } catch (error) {
        console.warn('Failed to get CSRF token:', error);
      }
    }
    
    // Merge headers properly - ensure CSRF token and Authorization are always added
    // Don't set Content-Type for FormData - let the browser set it with boundary
    const mergedHeaders = {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
    };
    
    const config = {
      ...options,
      headers: mergedHeaders,
      credentials: 'include',
    };
    
    let response = await monitoredFetch(url, config);
    
    // If we get a 403 and we have a CSRF token, it might be invalid - clear cache and retry once
    if (response.status === 403 && csrfToken && needsCsrfToken(method)) {
      console.warn('403 Forbidden with CSRF token, clearing cache and retrying...');
      const { clearCsrfToken } = await import('./csrfToken');
      clearCsrfToken();
      try {
        const newCsrfToken = await getCsrfToken();
        if (newCsrfToken && newCsrfToken !== csrfToken) {
          // Retry with new CSRF token
          const retryConfig = {
            ...config,
            headers: {
              ...config.headers,
              'x-csrf-token': newCsrfToken,
            },
          };
          response = await monitoredFetch(url, retryConfig);
        }
      } catch (error) {
        console.warn('Failed to refresh CSRF token on retry:', error);
      }
    }
    
    if (response.status === 401) {
      console.log('401 Unauthorized error detected, triggering session timeout handler...');
      if (window.handleGlobal401) {
        window.handleGlobal401();
      } else {
        console.warn('Global 401 handler not found, user may not be logged out automatically');
      }
    }
    
    // Return the raw response so the caller can call .json() on it
    // This matches the working pattern from AppointmentsPage.js
    return response;
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

// Global API client instance
export const apiClient = new ApiClient();

// Hook for API calls with monitoring
export const useApiClient = () => {
  return {
    get: apiClient.get.bind(apiClient),
    post: apiClient.post.bind(apiClient),
    put: apiClient.put.bind(apiClient),
    delete: apiClient.delete.bind(apiClient),
    patch: apiClient.patch.bind(apiClient),
  };
}; 